import React from 'react'
import { IUser } from '@/model/types/user'
import { useRequest } from 'ahooks'
import { IPagination } from '@/model/types/common'
import { $post } from '@/api'
import Table, { ColumnsType } from 'antd/lib/table'
import { getBEMCls } from '@/utils/classname'
import { WithScrollLoad, AutoResizer } from '@/utils/utils-widget'

import './purchase-table.scss'
import $msg from '@/utils/message'
import { showLayer } from '@/utils/layer'
import PurchaseDetail from '../purchase-detail/purchase-detail'
import { purchaseOrderStore } from '../../model'
import { observer } from 'mobx-react'
import EditPurchase from '../edit-purchase/edit-purchase'
import { staffStore } from '@/store/staff'

interface ICreator {
    id: number
    user: IUser
}

interface IStockInOrder {
    id: number
    creator: ICreator
    order_no: string
    eta: string
    stocking_in_amount: number
    stocked_in: boolean
    stock_in_time: string
    stocked_in_amount: number
    purchase_order_id: number
    purchase_time: string
    order_goods_count: number
}

export interface IPurchaseOrder {
    id: number
    creator: ICreator
    order_no: string
    is_draft: boolean
    order_goods_count: number
    purchase_subtotal: number | null
    eta: string
    stocked_state: number
    stock_in_order: IStockInOrder[]
    purchase_time: string
}

interface PurchaseOrderTableList {
    list: IPurchaseOrder[]
    pagination: IPagination
}

interface PurchaseTableProps {
    dateString: string[]
}

const PurchaseTable: React.FC<PurchaseTableProps> = observer(props => {
    const hasCostPermission = !!staffStore.canIUse(39)
    const cls = (...args: string[]) => getBEMCls('purchase-table', ...args)

    const requestHandle = useRequest<
        PurchaseOrderTableList,
        {
            orders: IPurchaseOrder[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/warehouse/getshoppurchaseorders',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    purchase_from_date: props.dateString[0] || void 0,
                    purchase_to_date: props.dateString[1] || void 0,
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.orders,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
            refreshDeps: [props.dateString, purchaseOrderStore.updateSatus],
        },
    )

    const { data, loading } = requestHandle

    const columns: ColumnsType<IPurchaseOrder> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '采购时间',
            key: 'purchase_time',
            dataIndex: 'purchase_time',
        },
        {
            title: '商品数',
            key: 'order_goods_count',
            dataIndex: 'order_goods_count',
        },
        {
            title: '采购金额',
            key: 'purchase_subtotal',
            dataIndex: 'purchase_subtotal',
            align: 'right',
            render: val => `¥${val}`,
        },
        {
            title: '到货时间',
            key: 'eta',
            dataIndex: 'eta',
        },
        {
            title: '状态',
            key: 'stocked_state',
            dataIndex: 'stocked_state',
            width: 80,
            render: (val, record) => {
                if (record.is_draft) {
                    return <span className="danger-color">草稿</span>
                } else {
                    switch (val) {
                        case 0:
                            return <span className="them-color">待入库</span>
                        case 1:
                            return <span className="them-color">部分入库</span>
                        case 2:
                            return <span>已入库</span>
                    }
                }
            },
        },
        {
            title: '操作',
            key: 'is_draft',
            width: 70,
            dataIndex: 'is_draft',
            render: (val, record) => (
                <span
                    className={cls('action')}
                    onClick={() => {
                        showLayer(val ? <EditPurchase orderId={record.id} /> : <PurchaseDetail orderId={record.id} />)
                    }}
                >
                    {val ? '编辑' : '详情'}
                </span>
            ),
        },
    ]

    return (
        <div className={cls('container')}>
            <AutoResizer>
                {({ height }) => {
                    return (
                        <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                            <Table
                                rowKey={record => record.id}
                                loading={loading || requestHandle.loadingMore || void 0}
                                bordered
                                pagination={false}
                                columns={
                                    hasCostPermission
                                        ? columns
                                        : columns.filter(column => column.key !== 'purchase_subtotal')
                                }
                                dataSource={data?.list}
                                scroll={{ x: 'max-content', y: height - 50 }}
                            />
                        </WithScrollLoad>
                    )
                }}
            </AutoResizer>
        </div>
    )
})

export default PurchaseTable
